(() => {
  'use strict';
  if (window.rmShoppingReady) return;
  window.rmShoppingReady = true;
  // Presentation only. Cart requests and duplicate protection remain in rm-commerce.js.
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const readSelection = inputs => {
    const rows = [];
    let count = 0, paidCount = 0, subtotal = 0, valid = true, priced = true;
    for (const input of inputs) {
      if (input.disabled) continue;
      const quantity = Number(input.value);
      const correct = String(input.value).trim() !== '' && Number.isSafeInteger(quantity) && quantity >= 0;
      if (!correct) { valid = false; continue; }
      if (quantity === 0) continue;
      const raw = input.dataset.priceCents;
      const price = Number(raw);
      const priceValid = /^\d+$/.test(raw || '') && Number.isSafeInteger(price) && price >= 0;
      const amount = price * quantity;
      count += quantity;
      if (priceValid && price > 0) paidCount += quantity;
      if (!priceValid || !Number.isSafeInteger(amount)) priced = false;
      else subtotal += amount;
      const title = input.dataset.productTitle || '';
      const variant = input.dataset.variantTitle || '';
      rows.push({ input, quantity, amount: priceValid ? amount : null, title: variant ? `${title} \u2014 ${variant}` : title });
    }
    if (!Number.isSafeInteger(count) || !Number.isSafeInteger(paidCount)) valid = false;
    if (!Number.isSafeInteger(subtotal)) priced = false;
    return { rows, count, paidCount, subtotal: priced ? subtotal : null, valid };
  };
  const currencyFormatter = currency => {
    try {
      if (!/^[A-Z]{3}$/.test(currency || '')) return null;
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'es-MX', { style: 'currency', currency, currencyDisplay: 'code', minimumFractionDigits: 0, maximumFractionDigits: 2 });
      return cents => Number.isSafeInteger(cents) ? formatter.format(cents / 100) : 'Consulta en el carrito';
    } catch { return null; }
  };
  const inputEvent = input => input.dispatchEvent(new Event('input', { bubbles: true }));
  const locked = form => form?.dataset.locked === 'true';
  function initQuantity(scope) {
    scope.querySelectorAll('[data-rm-step]').forEach(button => {
      if (button.dataset.rmStepReady) return;
      button.dataset.rmStepReady = 'true';
      const input = button.closest('.rm-qty-control')?.querySelector('input[type="number"]');
      if (!input) return;
      button.hidden = false;
      const sync = () => { button.disabled = input.disabled || locked(input.form); };
      sync();
      button.addEventListener('click', () => {
        if (input.disabled || input.readOnly || locked(input.form)) return;
        const min = Number(input.min || 0), max = input.max ? Number(input.max) : Number.MAX_SAFE_INTEGER;
        const step = Number(input.step || 1), current = Number(input.value);
        const next = (Number.isSafeInteger(current) ? current : min) + Number(button.dataset.rmStep) * step;
        if (!Number.isSafeInteger(next) || next < min || next > max) return;
        input.value = String(next); inputEvent(input);
      });
    });
  }
  function initGallery(scope) {
    scope.querySelectorAll('[data-rm-gallery]').forEach(gallery => {
      if (gallery.dataset.rmGalleryReady) return;
      gallery.dataset.rmGalleryReady = 'true';
      const thumbs = Array.from(gallery.querySelectorAll('[data-rm-gallery-image]'));
      const main = gallery.querySelector('[data-rm-main-image] img');
      const zoom = gallery.querySelector('[data-rm-zoom]');
      const dialog = gallery.querySelector('[data-rm-lightbox]');
      const expanded = gallery.querySelector('[data-rm-lightbox-image]');
      if (!thumbs.length || !main || !zoom) return;
      let index = Math.max(0, thumbs.findIndex(thumb => thumb.getAttribute('aria-current') === 'true'));
      const updateExpanded = () => {
        if (!expanded || !dialog?.open) return;
        expanded.src = thumbs[index].href;
        expanded.alt = thumbs[index].dataset.alt || '';
        const position = gallery.querySelector('[data-rm-image-position]');
        if (position) position.textContent = `${index + 1} / ${thumbs.length}`;
      };
      const show = nextIndex => {
        index = (nextIndex + thumbs.length) % thumbs.length;
        const thumb = thumbs[index];
        main.src = thumb.dataset.src;
        if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
        else main.removeAttribute('srcset');
        main.alt = thumb.dataset.alt || '';
        for (const key of ['width', 'height']) {
          if (/^[1-9]\d*$/.test(thumb.dataset[key] || '')) main.setAttribute(key, thumb.dataset[key]);
        }
        zoom.href = thumb.href;
        thumbs.forEach((item, i) => { if (i === index) item.setAttribute('aria-current', 'true'); else item.removeAttribute('aria-current'); });
        const caption = gallery.querySelector('[data-rm-gallery-caption]');
        if (caption) caption.textContent = `Imagen ${index + 1} de ${thumbs.length}. Pulsa la imagen para ampliarla.`;
        updateExpanded();
      };
      thumbs.forEach((thumb, i) => thumb.addEventListener('click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); show(i);
      }));
      zoom.addEventListener('click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || typeof dialog?.showModal !== 'function') return;
        event.preventDefault(); dialog.showModal(); updateExpanded();
      });
      gallery.querySelector('[data-rm-close]')?.addEventListener('click', () => dialog?.close());
      gallery.querySelector('[data-rm-gallery-prev]')?.addEventListener('click', () => show(index - 1));
      gallery.querySelector('[data-rm-gallery-next]')?.addEventListener('click', () => show(index + 1));
      gallery.querySelectorAll('[data-rm-gallery-prev], [data-rm-gallery-next]').forEach(button => { button.disabled = thumbs.length < 2; });
      dialog?.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
      });
      dialog?.addEventListener('close', () => zoom.focus());
      const variant = gallery.closest('[data-rm-product]')?.querySelector('[data-rm-variant]');
      const syncVariantImage = () => {
        const imageId = variant?.selectedOptions[0]?.dataset.imageId;
        const next = thumbs.findIndex(thumb => thumb.dataset.imageId === imageId);
        if (next >= 0) show(next);
      };
      variant?.addEventListener('change', syncVariantImage);
      show(index); syncVariantImage();
    });
  }
  function initSelector(scope) {
    scope.querySelectorAll('[data-rm-selector]').forEach(form => {
      if (form.dataset.rmSelectorReady) return;
      form.dataset.rmSelectorReady = 'true';
      const inputs = Array.from(form.querySelectorAll('[data-rm-quantity]'));
      const cards = Array.from(form.querySelectorAll('[data-rm-selector-card]'));
      const search = form.querySelector('[data-rm-selector-search]');
      const selectedOnly = form.querySelector('[data-rm-selected-only]');
      const format = currencyFormatter(form.dataset.currency);
      const money = cents => format && cents !== null ? format(cents) : 'Consulta en el carrito';
      let selectedFilter = false;
      cards.forEach(card => { card.dataset.rmSearch = normalize(card.textContent); });
      form.querySelectorAll('[data-rm-selector-tools], [data-rm-selection-tools]').forEach(node => { node.hidden = false; });
      const applyFilter = () => {
        const query = normalize(search?.value);
        let visible = 0;
        cards.forEach(card => {
          const chosen = Array.from(card.querySelectorAll('[data-rm-quantity]')).some(input => !input.disabled && Number(input.value) > 0);
          const matches = query.split(/\s+/).every(word => card.dataset.rmSearch.includes(word));
          card.hidden = !matches || (selectedFilter && !chosen);
          if (!card.hidden) { visible++; if (query || selectedFilter) card.open = true; }
        });
        const result = form.querySelector('[data-rm-selector-results]');
        if (result) result.textContent = `${visible} ${visible === 1 ? 'producto' : 'productos'}`;
        const empty = form.querySelector('[data-rm-selector-empty]');
        if (empty) empty.hidden = visible > 0;
        const reset = form.querySelector('[data-rm-reset-filter]');
        if (reset) reset.hidden = !query && !selectedFilter;
      };
      const update = () => {
        const selection = readSelection(inputs);
        const count = form.querySelector('[data-rm-selection-count]');
        if (count) count.textContent = selection.valid ? `${selection.count} ${selection.count === 1 ? 'pieza seleccionada' : 'piezas seleccionadas'}` : 'Revisa las cantidades: usa n\u00fameros enteros a partir de cero.';
        const subtotal = form.querySelector('[data-rm-selection-subtotal]');
        if (subtotal) subtotal.textContent = selection.valid ? money(selection.subtotal) : '\u2014';
        const progress = form.querySelector('[data-rm-selection-progress]');
        if (progress) progress.value = selection.valid ? Math.min(3, selection.paidCount) : 0;
        const shipping = form.querySelector('[data-rm-selection-shipping]');
        const missing = Math.max(0, 3 - selection.paidCount);
        if (shipping) shipping.textContent = !selection.valid ? 'Corrige las cantidades para revisar tu selecci\u00f3n.' : missing > 0
          ? `Elige ${missing} ${missing === 1 ? 'pieza m\u00e1s' : 'piezas m\u00e1s'} para reunir 3 mapas o bit\u00e1coras de la promoci\u00f3n de env\u00edo econ\u00f3mico gratis en M\u00e9xico.`
          : 'Esta selecci\u00f3n re\u00fane las piezas para el env\u00edo econ\u00f3mico gratis en M\u00e9xico. Confirma la cobertura y las condiciones antes de pagar.';
        const list = form.querySelector('[data-rm-selection-lines]');
        if (list) {
          list.replaceChildren();
          for (const row of selection.rows) {
            const li = document.createElement('li'), description = document.createElement('span'), price = document.createElement('strong');
            description.textContent = `${row.quantity} \u00d7 ${row.title}`; price.textContent = money(row.amount);
            li.append(description, price); list.append(li);
          }
          if (!selection.rows.length) { const li = document.createElement('li'); li.textContent = 'A\u00fan no has elegido productos.'; list.append(li); }
        }
        cards.forEach(card => {
          const value = readSelection(Array.from(card.querySelectorAll('[data-rm-quantity]')));
          const badge = card.querySelector('[data-rm-card-count]');
          if (badge) { badge.hidden = !value.count || !value.valid; badge.textContent = `${value.count} ${value.count === 1 ? 'pieza' : 'piezas'}`; }
        });
        const submit = form.querySelector('[data-rm-submit]');
        if (submit && !locked(form)) submit.disabled = !selection.valid || selection.count === 0;
        const clear = form.querySelector('[data-rm-clear-selection]');
        if (clear) clear.disabled = locked(form) || (selection.valid && selection.count === 0);
        applyFilter();
      };
      search?.addEventListener('input', applyFilter);
      search?.addEventListener('keydown', event => { if (event.key === 'Enter') event.preventDefault(); });
      selectedOnly?.addEventListener('click', () => { selectedFilter = !selectedFilter; selectedOnly.setAttribute('aria-pressed', String(selectedFilter)); applyFilter(); });
      form.querySelector('[data-rm-reset-filter]')?.addEventListener('click', () => { if (search) search.value = ''; selectedFilter = false; selectedOnly?.setAttribute('aria-pressed', 'false'); applyFilter(); });
      form.querySelector('[data-rm-clear-selection]')?.addEventListener('click', () => { if (locked(form)) return; inputs.forEach(input => { input.value = '0'; }); inputEvent(form); });
      form.addEventListener('input', event => { if (event.target === form || event.target.matches('[data-rm-quantity]')) update(); });
      form.addEventListener('invalid', event => { const card = event.target.closest('[data-rm-selector-card]'); if (card) { card.hidden = false; card.open = true; } }, true);
      form.addEventListener('submit', () => { if (locked(form)) { inputs.forEach(input => { input.readOnly = true; }); form.querySelectorAll('[data-rm-step], [data-rm-clear-selection]').forEach(button => { button.disabled = true; }); } });
      form.rmRefreshSelection = () => { inputs.forEach(input => { input.readOnly = locked(form); }); form.querySelectorAll('[data-rm-step]').forEach(button => { const input = button.closest('.rm-qty-control')?.querySelector('input'); button.disabled = locked(form) || !!input?.disabled; }); update(); };
      update();
    });
  }
  function init(scope = document) { initQuantity(scope); initGallery(scope); initSelector(scope); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.addEventListener('pageshow', event => { if (event.persisted) document.querySelectorAll('[data-rm-selector]').forEach(form => form.rmRefreshSelection?.()); });
})();
